import { AnimatePresence, motion } from 'framer-motion';
import noop from 'lodash/noop';
import type { ChangeEvent, CSSProperties, FocusEvent, FunctionComponent, ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useApplicationContext } from '~/shared/context/use-application-context';
import { useTranslate } from '~/shared/context/use-translate';
import { Button } from '~/ui/core/button/Button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '~/ui/core/dropdown-menu/DropdownMenu';
import { BOARD_COLUMN_CONTENT_SELECTOR } from '~/ui/core/focus/focus-regions';
import { IconArrowUp, IconExpand45, IconFile, IconFolder, IconPlus, IconStop, MicrophoneIcon } from '~/ui/core/icons';
import { Textarea } from '~/ui/core/textarea/Textarea';
import { Tooltip } from '~/ui/core/tooltip/Tooltip';
import { theme } from '~/ui/theme/cobrainer-theme';

import { useChatAttachmentOptions } from '../api/use-chat-attachment-options';
import { COMPOSER_TRANSITION } from '../constants';
import { hasAcceptedExtension, useAttachmentDrop } from '../hooks/use-attachment-drop';
import type { UseChatTranscriptionReturn } from '../hooks/use-chat-transcription';
import { useDismissOnOutsideMousedown } from '../hooks/use-dismiss-on-outside-mousedown';
import { useScrollDirection } from '../hooks/use-scroll-direction';
import { KnowledgeBaseSelectDialog, type SelectedKnowledgeBaseDocument } from './KnowledgeBaseSelectDialog';
import {
    CHAT_ATTACHMENT_ACCEPT,
    MAX_ATTACHMENT_COUNT,
    type PendingAttachment,
    PendingAttachmentList,
} from './PendingAttachmentList';
import { RecordingOverlay } from './RecordingOverlay';

export const PILL_COMPOSER_WIDTH_PX = 432;
export const PILL_COMPOSER_WIDTH_CSS = `min(${PILL_COMPOSER_WIDTH_PX}px, calc(100vw - 32px))`;

const DEFAULT_BORDER_COLOR = 'var(--color-border-subtle)';
const ACTIVE_BORDER_COLOR = 'var(--color-border-control-focus)';

const COLLAPSED_BG = 'var(--color-surface-raised)';
const COLLAPSED_BG_RECORDING = 'var(--color-surface-recording)';
const EXPANDED_BG = 'var(--color-surface-raised)';

const PILL_SHADOW = 'var(--shadow-md)';
const PILL_BORDER_COLOR = 'transparent';

const PILL_BORDER_RADIUS = theme.borderRadius['4xl'];
const EXPANDED_BORDER_RADIUS = theme.borderRadius.xl;

const PILL_PADDING = { paddingTop: 10, paddingRight: 12, paddingBottom: 10, paddingLeft: 20 } as const;
const PILL_PADDING_WITH_ATTACH_BUTTON = { ...PILL_PADDING, paddingLeft: 12 } as const;
const EXPANDED_PADDING = { paddingTop: 6, paddingRight: 10, paddingBottom: 8, paddingLeft: 10 } as const;

const PILL_TRANSITION = COMPOSER_TRANSITION;

// Deliberately NOT using framer-motion `layout` here — the composer cross-fades
// against the tile pill (no shared layoutId morph). `layout` would re-animate
// on unrelated dock reflows (e.g. sidepanel added), making the composer lag.
const CONTAINER_BLOOM_TRANSITION = { duration: 0.22, ease: [0.215, 0.61, 0.355, 1] } as const;

const PILL_TEXTAREA_ROWS = 1;
const PILL_TEXTAREA_MAX_ROWS = 6;
const EXPANDED_TEXTAREA_ROWS = 2;
const EXPANDED_TEXTAREA_MAX_ROWS = 8;

const PILL_TEXTAREA_CLASS =
    'text-body-s font-medium text-text-form placeholder:overflow-hidden placeholder:text-ellipsis placeholder:whitespace-nowrap placeholder:text-text-content-faint';
const EXPANDED_TEXTAREA_CLASS =
    'text-body-s font-medium text-text-content-strong placeholder:text-text-disabled flex-1';

const resolveCollapsedBackground = (hideInput: boolean): string => (hideInput ? COLLAPSED_BG_RECORDING : COLLAPSED_BG);

const resolveExpandedBorder = (isActive: boolean): string => (isActive ? ACTIVE_BORDER_COLOR : DEFAULT_BORDER_COLOR);

const computeContainerAnimate = (isExpanded: boolean, hideInput: boolean, isActive: boolean) => ({
    backgroundColor: isExpanded ? EXPANDED_BG : resolveCollapsedBackground(hideInput),
    borderColor: isExpanded ? resolveExpandedBorder(isActive) : PILL_BORDER_COLOR,
    borderRadius: isExpanded ? EXPANDED_BORDER_RADIUS : PILL_BORDER_RADIUS,
    borderWidth: isExpanded ? 1 : 0,
    boxShadow: isExpanded ? 'none' : PILL_SHADOW,
});

// Padding on inner row (not container) so `topSlot` can span pill edge-to-edge
// with its own divider.
const computeInnerAnimate = (isExpanded: boolean, canAttachDocuments: boolean) => {
    if (isExpanded) return EXPANDED_PADDING;
    return canAttachDocuments ? PILL_PADDING_WITH_ATTACH_BUTTON : PILL_PADDING;
};

// Radix-portaled surfaces treated as extensions of the pill (not outside clicks), plus board column content so
// clicks in a board column do not collapse the composer.
const SPAWNED_OVERLAY_SELECTOR = [
    '[role="dialog"]',
    '[role="alertdialog"]',
    '[role="menu"]',
    '[role="listbox"]',
    BOARD_COLUMN_CONTENT_SELECTOR,
    '[data-radix-popper-content-wrapper]',
].join(',');

const isWithinSpawnedOverlay = (target: Element): boolean => target.closest(SPAWNED_OVERLAY_SELECTOR) != null;

const CONTAINER_STATIC_STYLE: CSSProperties = {
    borderStyle: 'solid',
};

const DRAG_ACTIVE_STYLE: CSSProperties = {
    outline: '1.5px dashed var(--color-border-drop-active)',
    outlineOffset: -1.5,
};

// Expanded pill fills the chat card; collapsed keeps the 432px desktop
// footprint while respecting narrow viewports.
const resolveContainerWidth = (isExpanded: boolean): CSSProperties['width'] =>
    isExpanded ? '100%' : PILL_COMPOSER_WIDTH_CSS;

type ComposerTextareaProps = {
    readonly draft: string;
    readonly inputTransitionStyle: CSSProperties;
    readonly isExpanded: boolean;
    readonly onDraftChange: (event: ChangeEvent<HTMLTextAreaElement>) => void;
    readonly onKeyDown: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
    readonly placeholder?: string;
};

const ComposerAttachButton: FunctionComponent<{
    readonly attachedDocuments: readonly SelectedKnowledgeBaseDocument[];
    readonly disabled: boolean;
    readonly inputTransitionStyle: CSSProperties;
    readonly pendingBytes: number;
    readonly pendingCount: number;
    readonly onFilesSelected: (files: readonly File[]) => void;
    readonly onKnowledgeBaseSelected: (documents: readonly SelectedKnowledgeBaseDocument[]) => void;
}> = ({
    attachedDocuments,
    disabled,
    inputTransitionStyle,
    pendingBytes,
    pendingCount,
    onFilesSelected,
    onKnowledgeBaseSelected,
}) => {
    const translate = useTranslate();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isKnowledgeBaseDialogOpen, setKnowledgeBaseDialogOpen] = useState(false);

    const handleFilesPicked = (event: ChangeEvent<HTMLInputElement>): void => {
        const picked = Array.from(event.target.files ?? []).filter(hasAcceptedExtension);
        event.target.value = '';
        if (picked.length > 0) onFilesSelected(picked);
    };

    const attachMenu = (
        <DropdownMenu>
            <DropdownMenuTrigger disabled={disabled}>
                <Button
                    aria-label={translate('common.add')}
                    disabled={disabled}
                    iconOnly={true}
                    size="sm"
                    variant="secondary"
                >
                    <IconPlus size={12} />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" sideOffset={4}>
                <DropdownMenuItem className="text-body-xxs!" onSelect={() => fileInputRef.current?.click()}>
                    <IconFile size={16} />
                    {translate('pages.job-architecture-v2.create-items.upload.upload-new')}
                </DropdownMenuItem>
                <DropdownMenuItem className="text-body-xxs!" onSelect={() => setKnowledgeBaseDialogOpen(true)}>
                    <IconFolder size={16} />
                    {translate('pages.job-architecture-v2.create-items.upload.select-from-knowledge-base')}
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );

    return (
        <motion.div className="flex shrink-0 items-center" style={inputTransitionStyle} transition={PILL_TRANSITION}>
            {disabled ? (
                <Tooltip>
                    <Tooltip.Trigger asChild={true}>
                        {/* The disabled button swallows pointer events, so the wrapper span carries the hover. */}
                        {/* biome-ignore lint/a11y/noNoninteractiveTabindex: the limit tooltip opens on focus, so keyboard users need to be able to focus the trigger. */}
                        <span tabIndex={0}>{attachMenu}</span>
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                        <Tooltip.Content side="top">
                            {translate('pages.job-architecture-v2.create-items.upload.limit-tooltip', {
                                count: MAX_ATTACHMENT_COUNT,
                            })}
                        </Tooltip.Content>
                    </Tooltip.Portal>
                </Tooltip>
            ) : (
                attachMenu
            )}
            <input
                ref={fileInputRef}
                accept={CHAT_ATTACHMENT_ACCEPT}
                className="hidden"
                multiple={true}
                type="file"
                onChange={handleFilesPicked}
            />
            <KnowledgeBaseSelectDialog
                attachedDocuments={attachedDocuments}
                isOpen={isKnowledgeBaseDialogOpen}
                pendingBytes={pendingBytes}
                pendingCount={pendingCount}
                onAttach={onKnowledgeBaseSelected}
                onOpenChange={setKnowledgeBaseDialogOpen}
            />
        </motion.div>
    );
};

const ComposerTextarea: FunctionComponent<ComposerTextareaProps> = ({
    draft,
    inputTransitionStyle,
    isExpanded,
    onDraftChange,
    onKeyDown,
    placeholder,
}) => {
    const translate = useTranslate();
    const label = placeholder ?? translate('chat.composer-placeholder');
    const wrapperClass = isExpanded
        ? 'w-full min-w-0 basis-full order-[-1] flex-1 flex self-center'
        : 'min-w-0 flex-1 flex align-center justify-center';
    const textareaClass = isExpanded ? EXPANDED_TEXTAREA_CLASS : PILL_TEXTAREA_CLASS;
    const textareaStyle = isExpanded ? inputTransitionStyle : { marginBottom: 4, ...inputTransitionStyle };

    return (
        <motion.div className={wrapperClass} transition={PILL_TRANSITION}>
            <Textarea
                autoResize={true}
                aria-label={label}
                className={textareaClass}
                maxRows={isExpanded ? EXPANDED_TEXTAREA_MAX_ROWS : PILL_TEXTAREA_MAX_ROWS}
                placeholder={label}
                rows={isExpanded ? EXPANDED_TEXTAREA_ROWS : PILL_TEXTAREA_ROWS}
                style={textareaStyle}
                value={draft}
                variant="ghost"
                onChange={onDraftChange}
                onKeyDown={onKeyDown}
            />
        </motion.div>
    );
};

const ComposerStopButton: FunctionComponent<{ readonly onStop: () => void }> = ({ onStop }) => {
    const translate = useTranslate();
    return (
        <Button
            aria-label={translate('chat.stop')}
            iconOnly={true}
            size="sm"
            type="button"
            variant="primary"
            onClick={onStop}
        >
            <IconStop size={12} aria-hidden={true} />
        </Button>
    );
};

// An input override (a question, the closing prompt) replaces the action buttons, but never Stop.
const OverrideStopButton: FunctionComponent<{ readonly canStop: boolean; readonly onStop?: () => void }> = ({
    canStop,
    onStop,
}) => (canStop && onStop ? <ComposerStopButton onStop={onStop} /> : null);

type ComposerButtonsProps = {
    readonly canStop: boolean;
    readonly hasDraft: boolean;
    readonly idleSlot?: ReactNode;
    readonly inputTransitionStyle: CSSProperties;
    readonly isExpanded: boolean;
    readonly isOpen: boolean;
    readonly isStreaming: boolean;
    readonly transcription: UseChatTranscriptionReturn;
    readonly draft: string;
    readonly onOpen?: () => void;
    readonly onStop?: () => void;
    readonly onSubmit?: () => void;
};

const ComposerButtons: FunctionComponent<ComposerButtonsProps> = ({
    canStop,
    draft,
    hasDraft,
    idleSlot,
    inputTransitionStyle,
    isExpanded,
    isOpen,
    isStreaming,
    transcription,
    onOpen,
    onStop,
    onSubmit,
}) => {
    const translate = useTranslate();
    const className = isExpanded ? 'flex shrink-0 items-center gap-xs ml-auto' : 'flex shrink-0 items-center gap-m';
    const isActionDisabled = isStreaming || (hasDraft && !draft.trim());

    return (
        <div className={className} style={inputTransitionStyle}>
            {!isExpanded && !isOpen && idleSlot}
            {!isExpanded && onOpen ? (
                <motion.div animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    <Button
                        aria-label={translate('chat.expand')}
                        iconOnly={true}
                        size="sm"
                        variant="secondary"
                        // Suppress focus-on-mousedown so clicking expand goes straight
                        // to the expanded chat without a flash of topSlot opening.
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={onOpen}
                    >
                        <IconExpand45 size={12} style={{ transform: 'rotate(270deg)' }} />
                    </Button>
                </motion.div>
            ) : null}
            <motion.div transition={PILL_TRANSITION}>
                {canStop && onStop ? (
                    <ComposerStopButton onStop={onStop} />
                ) : (
                    <Button
                        aria-label={hasDraft ? translate('chat.send') : translate('chat.record')}
                        disabled={isActionDisabled}
                        className="hover:opacity-100 disabled:opacity-40"
                        iconOnly={true}
                        size="sm"
                        type="button"
                        variant="primary"
                        onClick={hasDraft ? (onSubmit ?? noop) : transcription.toggleRecording}
                    >
                        {hasDraft ? <IconArrowUp size={12} aria-hidden={true} /> : <MicrophoneIcon />}
                    </Button>
                )}
            </motion.div>
        </div>
    );
};

type PillChatComposerProps = {
    /** A turn is live here or on the server, so the action becomes Stop. */
    readonly canStop?: boolean;
    readonly draft: string;
    readonly isExpanded: boolean;
    /** When true, the send/record action is disabled (a turn is in-flight). */
    readonly isStreaming?: boolean;
    readonly placeholder?: string;
    readonly transcription: UseChatTranscriptionReturn;
    readonly onDraftChange: (event: ChangeEvent<HTMLTextAreaElement>) => void;
    readonly onKeyDown: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
    readonly onOpen?: () => void;
    // Open is sticky across blurs — only closes on explicit gestures (click outside,
    // scroll down). Lets parents render open-driven affordances (e.g. focus gradient).
    readonly onOpenChange?: (open: boolean) => void;
    /** Shown in place of the send/record action while `canStop` holds. */
    readonly onStop?: () => void;
    readonly onSubmit?: () => void;
    readonly topSlot?: ReactNode;
    /** Rendered inline in the collapsed pill, left of the action buttons. */
    readonly idleSlot?: ReactNode;
    /**
     * Incrementing counter — every change opens the topSlot. Lets parents
     * imperatively reveal the pill without taking over the open/close state.
     */
    readonly openRequestSignal?: number;
    /** Opens the topSlot when the textarea gains focus. */
    readonly openOnFocus?: boolean;
    /** When set, replaces the textarea + send/record buttons row entirely. */
    readonly inputOverride?: ReactNode;
    /** Documents picked for the next message, rendered as pending cards above the input row. */
    readonly attachments?: readonly PendingAttachment[];
    readonly onAttachmentFilesSelected?: (files: readonly File[]) => void;
    readonly onAttachmentKnowledgeBaseSelected?: (documents: readonly SelectedKnowledgeBaseDocument[]) => void;
    readonly onAttachmentRemove?: (fileName: string) => void;
    readonly onAttachmentToggleKnowledgeBase?: (fileName: string, addToKnowledgeBase: boolean) => void;
};

export const PillChatComposer: FunctionComponent<PillChatComposerProps> = ({
    canStop = false,
    draft,
    isExpanded,
    isStreaming = false,
    placeholder,
    transcription,
    onDraftChange,
    onKeyDown,
    onOpen,
    onOpenChange,
    onStop,
    onSubmit,
    topSlot,
    idleSlot,
    openRequestSignal,
    openOnFocus = false,
    inputOverride,
    attachments = [],
    onAttachmentFilesSelected,
    onAttachmentKnowledgeBaseSelected,
    onAttachmentRemove,
    onAttachmentToggleKnowledgeBase,
}) => {
    const hasDraft = draft.trim().length > 0;
    const hideInput = transcription.isRecording || transcription.isTranscribing;
    // Fade input row out (keep mounted) so RecordingOverlay owns the surface
    // while preserving draft/focus state across a recording round-trip.
    const inputTransitionStyle: CSSProperties = {
        opacity: hideInput ? 0 : 1,
        pointerEvents: hideInput ? 'none' : 'auto',
        transition: 'opacity 180ms ease',
    };

    // Callback ref so observation re-attaches if the topSlot wrapper remounts.
    // Tween targets a measured px height — framer's `height: 'auto'` resolves
    // mid-tween but settles to CSS auto, producing a sub-pixel snap.
    const [topSlotNode, setTopSlotNode] = useState<HTMLDivElement | null>(null);
    const [topSlotHeight, setTopSlotHeight] = useState(0);

    useEffect(() => {
        if (topSlotNode == null) {
            return;
        }
        const observer = new ResizeObserver(([entry]) => {
            if (entry != null) {
                setTopSlotHeight(entry.contentRect.height);
            }
        });
        observer.observe(topSlotNode);
        return () => observer.disconnect();
    }, [topSlotNode]);

    const [isHovered, setIsHovered] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const { applicationState } = useApplicationContext();
    // The api reports upload capability per caller; while it loads (or if it fails) the attach
    // affordance stays hidden, matching the knowledge-base toggle's safe default.
    const { data: attachmentOptions } = useChatAttachmentOptions();
    const canAttachDocuments =
        applicationState.featureFlagService.isFeatureFlagEnabled('chat-attachments') &&
        attachmentOptions?.canUpload === true;
    const { dropHandlers, isDragActive } = useAttachmentDrop({
        enabled: canAttachDocuments,
        onFiles: onAttachmentFilesSelected ?? noop,
    });
    // Sticky across textarea blur — topSlot button clicks must not dismiss it.
    // Opens only from explicit requests. Closes on click-outside or scroll-down.
    const [isOpen, setIsOpen] = useState(false);
    // Gates scroll-up auto-reopen so fresh page loads don't surface topSlot
    // uninvited. Reset on click-outside to also disarm the reveal.
    const hasOpenedOnceRef = useRef(false);

    // External requests to expand the pill (e.g. clicking an idle-slot
    // affordance). Each new signal value reopens; skipped on first mount so
    // a stale initial value can't surface the topSlot uninvited.
    const lastOpenRequestRef = useRef(openRequestSignal);
    useEffect(() => {
        if (openRequestSignal === undefined || openRequestSignal === lastOpenRequestRef.current) {
            return;
        }
        lastOpenRequestRef.current = openRequestSignal;
        setIsOpen(true);
        hasOpenedOnceRef.current = true;
    }, [openRequestSignal]);

    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        onOpenChange?.(isOpen);
    }, [isOpen, onOpenChange]);

    const handleOutsideDismiss = useCallback(() => {
        setIsOpen(false);
        hasOpenedOnceRef.current = false;
    }, []);

    useDismissOnOutsideMousedown({
        active: isOpen,
        containerRef,
        isInsideAllowedExtension: isWithinSpawnedOverlay,
        onDismiss: handleOutsideDismiss,
    });

    const handleScrollDown = useCallback(() => setIsOpen(false), []);
    const handleScrollUp = useCallback(() => {
        if (hasOpenedOnceRef.current) {
            setIsOpen(true);
        }
    }, []);

    useScrollDirection({ onScrollDown: handleScrollDown, onScrollUp: handleScrollUp });

    const showTopSlot = topSlot != null && (isExpanded || isOpen) && !isStreaming;

    const innerClass = isExpanded ? 'relative flex w-full flex-wrap items-end' : 'relative flex flex-wrap items-end';
    const innerStyle = isExpanded ? { gap: 6 } : { columnGap: 12, rowGap: 24 };

    return (
        <motion.div
            ref={containerRef}
            className="font-sans"
            initial={false}
            animate={computeContainerAnimate(isExpanded, hideInput, isFocused || isHovered)}
            transition={CONTAINER_BLOOM_TRANSITION}
            style={{
                ...CONTAINER_STATIC_STYLE,
                width: resolveContainerWidth(isExpanded),
                ...(isDragActive ? DRAG_ACTIVE_STYLE : {}),
            }}
            {...dropHandlers}
            onBlur={(event: FocusEvent<HTMLDivElement>) => {
                const next = event.relatedTarget as Node | null;
                if (next != null && containerRef.current?.contains(next) === true) {
                    return;
                }
                setIsFocused(false);
            }}
            onFocus={(event: FocusEvent<HTMLDivElement>) => {
                event.stopPropagation();
                setIsFocused(true);
                if (openOnFocus && event.target instanceof HTMLTextAreaElement) {
                    setIsOpen(true);
                    hasOpenedOnceRef.current = true;
                }
            }}
            onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
                if (event.key === 'Escape' && !event.defaultPrevented) {
                    handleOutsideDismiss();
                }
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {topSlot != null && (
                <motion.div
                    className="overflow-hidden"
                    data-element="composer-top-slot"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{
                        height: showTopSlot ? topSlotHeight : 0,
                        opacity: showTopSlot ? 1 : 0,
                    }}
                    transition={CONTAINER_BLOOM_TRANSITION}
                >
                    <div ref={setTopSlotNode} className="border-b border-border-soft">
                        {topSlot}
                    </div>
                </motion.div>
            )}
            <motion.div
                className={innerClass}
                initial={false}
                animate={computeInnerAnimate(isExpanded, canAttachDocuments)}
                transition={CONTAINER_BLOOM_TRANSITION}
                style={innerStyle}
            >
                <AnimatePresence initial={false}>
                    {transcription.showOverlay && (
                        <RecordingOverlay
                            key="recording-overlay"
                            getAmplitude={transcription.getAmplitude}
                            isFadingOut={transcription.overlayFadingOut}
                            isTranscribing={transcription.isTranscribing}
                            recordingSeconds={transcription.recordingSeconds}
                            onStopRecording={transcription.stopRecording}
                        />
                    )}
                    {inputOverride != null ? (
                        <div key="composer-input-override" className="flex w-full min-w-0 items-end gap-xs">
                            <div className="min-w-0 flex-1">{inputOverride}</div>
                            <OverrideStopButton canStop={canStop} onStop={onStop} />
                        </div>
                    ) : (
                        <>
                            {canAttachDocuments && attachments.length > 0 && (
                                <motion.div
                                    key="composer-attachments"
                                    className="w-full basis-full"
                                    style={inputTransitionStyle}
                                    transition={PILL_TRANSITION}
                                >
                                    <PendingAttachmentList
                                        attachments={attachments}
                                        onRemove={onAttachmentRemove ?? noop}
                                        onToggleKnowledgeBase={onAttachmentToggleKnowledgeBase ?? noop}
                                    />
                                </motion.div>
                            )}
                            {canAttachDocuments && (
                                <ComposerAttachButton
                                    key="composer-attach"
                                    attachedDocuments={attachments
                                        .filter((it) => it.documentId !== undefined)
                                        .map((it) => ({
                                            id: it.documentId as string,
                                            name: it.fileName,
                                            sizeBytes: it.sizeBytes,
                                        }))}
                                    disabled={attachments.length >= MAX_ATTACHMENT_COUNT}
                                    inputTransitionStyle={inputTransitionStyle}
                                    pendingBytes={attachments
                                        .filter((it) => it.documentId === undefined)
                                        .reduce((sum, it) => sum + it.sizeBytes, 0)}
                                    pendingCount={attachments.filter((it) => it.documentId === undefined).length}
                                    onFilesSelected={onAttachmentFilesSelected ?? noop}
                                    onKnowledgeBaseSelected={onAttachmentKnowledgeBaseSelected ?? noop}
                                />
                            )}
                            <ComposerTextarea
                                draft={draft}
                                key="composer-textarea"
                                inputTransitionStyle={inputTransitionStyle}
                                isExpanded={isExpanded}
                                placeholder={placeholder}
                                onDraftChange={onDraftChange}
                                onKeyDown={onKeyDown}
                            />
                            <ComposerButtons
                                canStop={canStop}
                                draft={draft}
                                key="composer-buttons"
                                hasDraft={hasDraft}
                                idleSlot={idleSlot}
                                inputTransitionStyle={inputTransitionStyle}
                                isExpanded={isExpanded}
                                isOpen={isOpen}
                                isStreaming={isStreaming}
                                transcription={transcription}
                                onOpen={onOpen}
                                onStop={onStop}
                                onSubmit={onSubmit}
                            />
                        </>
                    )}
                </AnimatePresence>
            </motion.div>
        </motion.div>
    );
};
